import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import BASE_QUERY from '@/features/baseQuery';
import {
	collection,
	doc,
	updateDoc,
	query,
	getDocs,
	getDoc,
	startAfter,
	limit,
	orderBy,
	documentId,
	Timestamp,
} from 'firebase/firestore';
import { firestore, storage } from '@/utils/firebase';
import { safelyParseDate } from '@/utils/date';
import msalInstance from '@/app/msal';
import { getDownloadURL, ref, updateMetadata, uploadBytes } from 'firebase/storage';
import { brokerSignaturePath } from '@/utils/storagePaths';
import axios from 'axios';
import { queryByBrokerGroup } from '@/utils/firebaseService';
import { logAuditActions } from '@/features/auditActions/auditActions';

const getBrokerGroupArray = (user) =>
	Array.from(new Set([...(user?.brokerGroupArray ?? []), user?.brokerGroupId].filter(Boolean)));

export const getActingUser = async (dispatch, getState) => {
	const email = getState()?.msalAccount?.account?.username;
	return dispatch(
		userApi.endpoints.getUserDetails.initiate({ email }, { subscribe: false }),
	).unwrap();
};

const parseUserDoc = async (d) => {
	const data = d.data();
	let brokerGroupData = null;

	if (data?.brokerGroup) {
		try {
			const snapshot = await getDoc(data.brokerGroup);

			if (snapshot.exists()) {
				const { tenant, config, network, ...rest } = snapshot.data();

				brokerGroupData = {
					...rest,
					lastUpdated: safelyParseDate(rest.lastUpdated),
				};
			}
		} catch (error) {
			console.error('Error fetching brokerGroup:', error);
		}
	}

	const structuredData = {
		...data,
		...(brokerGroupData && { brokerGroupData }),
		...(data?.brokerGroup?.id && {
			brokerGroupId: data.brokerGroup.id,
		}),
	};
	//Spread JSON at the bottom, ensures all data is stringified...
	return {
		id: d.id,
		data: {
			...JSON.parse(JSON.stringify(structuredData)),
			brokerGroupArray: getBrokerGroupArray(structuredData),
		},
	};
};

const sanitiseSignature = (html) => {
	const doc = new DOMParser().parseFromString(html, 'text/html');

	doc.querySelectorAll('script').forEach((el) => el.remove());

	doc.querySelectorAll('*').forEach((el) => {
		[...el.attributes].forEach((attr) => {
			if (attr.name.startsWith('on')) el.removeAttribute(attr.name);
		});
	});

	const inner = doc.body.innerHTML.trim();

	if (!inner) return inner;

	const firstElement = [...doc.body.childNodes].find((n) => n.nodeType === Node.ELEMENT_NODE);

	const isDiv = firstElement?.nodeName?.toLowerCase() === 'div';
	if (isDiv) {
		firstElement.setAttribute('data-signature', 'true');
		return inner;
	}

	return `<div data-signature="true">${inner}</div>`;
	// console.log('DOC SIGNATURE', doc.body.innerHTML);
	// return doc.body.innerHTML;
};

const resolveFirebasePath = async (data) => {
	try {
		const segments = data?.['_path']?.segments;
		const collectionRef = collection(firestore, segments[0]);
		const ref = doc(collectionRef, segments[1]);
		return await getDoc(ref);
	} catch {
		return null;
	}
};

const isBrokerGroupSnapshot = (value) =>
	value != null && typeof value.data === 'function' && typeof value.id === 'string';

const safeSerialise = (value) => {
	if (value == null) return null;
	try {
		return JSON.parse(JSON.stringify(value));
	} catch {
		return null;
	}
};

const serialiseBrokerGroup = async (docOrData, full = false) => {
	const isSnapshot = isBrokerGroupSnapshot(docOrData);
	const raw = isSnapshot ? (docOrData.data() ?? {}) : (docOrData ?? {});
	// serialiseBrokerGroupData mutates; clone so snapshot/raw callers stay untouched
	const data = full ? await serialiseBrokerGroupData({ ...raw }, { resolveTenant: false }) : raw;
	return {
		...(safeSerialise(data) ?? {}),
		clientSite: data.clientSite ?? `${data?.sharepointDomain}${data?.clientSiteSuffix}`,
		sharepointSite: data.sharepointSite ?? data.sharepointDomain,
		id: isSnapshot ? docOrData.id : (data?.id ?? raw?.id),
	};
};

// resolveTenant: only the signed-in user's primary broker group needs its tenant
// (getUserDetails → tenantConsented). Other groups' tenants aren't readable under the
// Firestore rules and nothing uses them, so they're dropped.
const serialiseBrokerGroupData = async (broker, { resolveTenant = true } = {}) => {
	if (!broker) {
		return { tenantConsented: false };
	}

	if (!broker.clientSite) {
		broker.clientSite = `${broker?.sharepointDomain}${broker?.clientSiteSuffix}`;
	}
	if (!broker.sharepointSite) {
		broker.sharepointSite = broker?.sharepointDomain;
	}
	if (resolveTenant) {
		broker.tenantConsented = false;
	} else {
		delete broker.tenant;
	}
	if (broker.tenant) {
		try {
			const tenant = await resolveFirebasePath(broker.tenant);
			const tenantData = tenant?.exists() ? tenant.data() : null;
			if (tenantData) {
				broker.tenantConsented = Boolean(tenantData.consented);
				broker.tenant = safeSerialise(tenantData);
			}
		} catch (e) {
			console.log('Tenant data failed', e);
			broker.tenantConsented = false;
			broker.tenant = safeSerialise(broker.tenant);
		}
	}
	if (broker.config) {
		try {
			const config = await resolveFirebasePath(broker.config);
			const configData = config?.exists() ? config.data() : null;
			if (configData) {
				broker.config = safeSerialise(configData);
				broker.countryCode = broker.config?.countryCode ?? broker.countryCode;
			}
		} catch (e) {
			console.log('Config data failed', e);
			broker.config = safeSerialise(broker.config);
		}
	}
	if (broker.network) {
		try {
			const network = await resolveFirebasePath(broker.network);
			const networkData = network?.exists() ? network.data() : null;
			if (networkData) {
				broker.network = safeSerialise(networkData);
			}
		} catch (e) {
			console.log('Network data failed', e);
			broker.network = safeSerialise(broker.network);
		}
	}
	return safeSerialise(broker) ?? { tenantConsented: false };
};

export const userApi = createApi({
	reducerPath: 'userApi',
	tagTypes: ['USER', 'BROKER_GROUP', 'BROKER_GROUP_USERS', 'USER_SIGNATURE', 'USERS'],
	baseQuery: fetchBaseQuery(BASE_QUERY),
	keepUnusedDataFor: 300,
	endpoints: (builder) => ({
		// Defining an endpoint to get properties for user by email
		getUserDetails: builder.query({
			providesTags: (result, error, arg) => [
				{ type: 'USER', id: arg.email },
				{ type: 'USER', id: arg.hubspotId },
			],
			queryFn: async ({ email, hubspotId, userId }, { dispatch }, _options, baseQuery) => {
				try {
					const res = await baseQuery({
						url: process.env.REACT_APP_INIT_USER,
						method: 'POST',
						body: { email, hubspotId, userId },
					});
					// A failed lookup must not be cached as an empty user — every permission
					// check would then deny until the cache expires.
					if (res.error) return { error: res.error };

					const response = res?.data?.user;
					console.log(
						`👾 ~ User response for ${email ? email : (userId ?? hubspotId)}`,
						response,
					);
					let broker = response?.broker?.data;
					const ledger = broker?.ledger;

					// response is the raw backend payload — brokerGroupId/brokerGroupIds live
					// under response.data here, unlike the already-transformed userDetails
					// object the other getBrokerGroupArray callers in this file pass in.
					const brokerGroupArray = Array.from(
						new Set(
							[
								...(response?.data?.brokerGroupIds ?? []),
								response?.data?.brokerGroupId,
							].filter(Boolean),
						),
					);

					broker = await serialiseBrokerGroupData(broker);
					const tenantConsented = Boolean(broker.tenantConsented);

					let signature =
						response?.data?.emailSignatures?.[0]?.signature ??
						response?.data?.signatureHtmlUrl ??
						'';
					if (signature) {
						try {
							const signatureRes = await dispatch(
								userApi.endpoints.getUserSignature.initiate({
									url: signature,
									id: response.id,
								}),
							);
							// console.log('signature res: ', signatureRes);

							signature = signatureRes.data;
						} catch (e) {
							console.error('There was an error getting signature', e);
						}
					}

					let insightUrl = broker?.insightUrl?.toString() ?? '';
					if (insightUrl.endsWith('/')) {
						insightUrl = insightUrl.slice(0, -1);
					}

					const isWinbeatLedger = insightUrl?.toLowerCase()?.includes('winbeat');
					const data = {
						...(response?.data ?? {}),
						userId: response?.id,
						brokerId: response?.broker?.id,
						brokerGroupId: response?.broker?.id,
						broker: JSON.parse(JSON.stringify(broker ?? {})),
						brokerGroupArray,
						hubspotId: response?.data?.hubspotId,
						name: response?.data?.name,
						email: response?.data?.email,
						salesTeamId: response?.data?.salesTeamId,
						authorisedRepId: response?.data?.authorisedRepId,
						external: !['folio', 'cbnnztst'].includes(ledger),
						canViewGroup: response?.data?.canViewGroup,
						sharepointSite: broker?.sharepointDomain,
						sharepointSiteId: broker?.sharepointSiteId,
						clientSiteSuffix: broker?.clientSiteSuffix,
						clientFolder: broker?.clientFolder,
						clientFolderDriveId: broker?.clientFolderDriveId,
						clientFolderListId: broker?.clientFolderListId,
						clientSite: `${broker?.sharepointDomain}${broker?.clientSiteSuffix}`,
						token: response?.token,
						phoneNumber: response?.data?.phoneNumber,
						linkedIn: response?.data?.linkedIn,
						fspNumber: response?.data?.fspNumber,
						claimsEmail: broker?.claimsEmail,
						website: broker?.website,
						about: broker?.about,
						aboutUser: response?.data?.aboutUser,
						businessAddress: broker?.address,
						colours: broker?.colours,
						brokerAdmin: response?.data?.brokerAdmin,
						isFolioAdmin: response?.data?.isFolioAdmin ?? false,
						disclosureUrl: response?.data?.disclosureUrl,
						privacyStatement: broker?.privacyStatement,
						termsOfEngagement: broker?.termsOfEngagement,

						imageUrl: response?.data?.imageUrl,
						fullLogo: broker?.fullLogo,
						insightUrl,
						brokerSupportId: response?.data?.brokerSupportId,
						claimsAdviserId: response?.data?.claimsAdviserId,
						complianceAdviserId: response?.data?.complianceAdviserId,
						tenantConsented,
						signatureHtmlUrl:
							response?.data?.emailSignatures?.[0]?.signature ??
							response?.data?.signatureHtmlUrl,
						signature,
						isWinbeatLedger,
					};

					return { data };
				} catch (e) {
					console.error(
						`Error getting user details for email ${email} or hubspotId ${hubspotId}`,
						e,
					);
					return { error: e };
				}
			},
		}),
		getAdminUsers: builder.query({
			query: () => ({
				url: `${process.env.REACT_APP_FOLIO_ADMIN_API_ENDPOINT}/v1/users`,
			}),
			transformResponse: (response) => response?.users ?? [],
		}),
		getAdminBrokerGroups: builder.query({
			query: () => ({
				url: `${process.env.REACT_APP_FOLIO_ADMIN_API_ENDPOINT}/v1/broker-groups`,
			}),
			transformResponse: (response) => response?.brokerGroups ?? [],
		}),
		getUserSignature: builder.query({
			providesTags: (result, error, { id }) => {
				return [{ type: 'USER_SIGNATURE', id }];
			},
			queryFn: async ({ url, id }) => {
				console.log('Get user signature', url, id);
				try {
					const response = await axios.get(url, {
						responseType: 'text',
					});
					const html = sanitiseSignature(response.data);
					return { data: html };
				} catch (e) {
					console.error('Error retrieving user signature', e);
				}
				return { data: '' };
			},
		}),
		getBrokerGroupDetails: builder.query({
			providesTags: (result, error, { docId }) => {
				return [{ type: 'BROKER_GROUP', id: docId }];
			},
			queryFn: async ({ docId }) => {
				const collectionRef = collection(firestore, 'brokerGroup');
				const ref = doc(collectionRef, docId);
				const docSnapshot = await getDoc(ref);

				const brokerDoc = await serialiseBrokerGroup(docSnapshot, true);
				return { data: brokerDoc };
			},
		}),
		getBrokerGroups: builder.query({
			providesTags: (result, error, { ids, showAdmin }) =>
				ids.map((id) => ({
					type: 'BROKER_GROUP',
					id,
				})),
			queryFn: async ({ ids, showAdmin = false }) => {
				const collectionRef = collection(firestore, 'brokerGroup');

				if (showAdmin) {
					let lastId = false;
					const docs = [];
					const getBrokerGroups = async (prevId) => {
						console.log('getBrokerGroups prev id ', prevId);
						const constraints = [
							limit(100),
							orderBy(documentId()),
							...(prevId ? [startAfter(prevId)] : []),
						];
						const q = query(collectionRef, ...constraints);
						const querySnapshot = await getDocs(q);
						docs.push(
							...(await Promise.all(
								querySnapshot.docs.map(async (d) => await serialiseBrokerGroup(d)),
							)),
						);
						// docs.push(
						// 	...querySnapshot.docs.map((d) => ({
						// 		id: d.id,
						// 		...JSON.parse(JSON.stringify(d.data() ?? {})),
						// 	})),
						// );
						lastId = querySnapshot.docs[querySnapshot.docs.length - 1]?.id;
					};
					while (lastId != null) {
						await getBrokerGroups(lastId);
					}
					return { data: docs };
				}
				// Groups the viewer can't read (Firestore rules) are left out instead of failing
				// the whole list.
				const docs = await Promise.all(
					ids.map(async (docId) => {
						const ref = doc(collectionRef, docId);
						const docSnapshot = await getDoc(ref).catch(() => null);

						return docSnapshot ? await serialiseBrokerGroup(docSnapshot) : null;
					}),
				);

				return { data: docs.filter(Boolean) };
			},
		}),
		getUsersByBrokerGroupId: builder.query({
			providesTags: ['BROKER_GROUP_USERS'],
			queryFn: async (brokerGroupId, { getState, dispatch }) => {
				try {
					//added user BrokerGroupId so making this brokerGroupId instead of ref
					const userCollectionRef = collection(firestore, 'user');

					const docs = await queryByBrokerGroup({
						collectionRef: userCollectionRef,
						brokerGroupIds: [brokerGroupId].filter((x) => x),
					});

					const data = await Promise.all(docs.map(async (d) => await parseUserDoc(d)));
					return { data };
				} catch (e) {
					console.error(
						`Error getting broker group users by brokerGroupId ${brokerGroupId}`,
						e,
					);
					return { error: e };
				}
			},
		}),
		getBrokerGroupUsers: builder.query({
			providesTags: ['BROKER_GROUP_USERS'],
			queryFn: async ({ userId, showGroup }, { getState, dispatch }) => {
				const userDoc = await dispatch(
					userApi.endpoints.getUserDetails.initiate({ userId }),
				).unwrap();

				const brokerGroupArray = getBrokerGroupArray(userDoc);

				let userDocs = [];
				if (showGroup) {
					for (const brokerGroupId of brokerGroupArray) {
						try {
							const docs = await dispatch(
								userApi.endpoints.getUsersByBrokerGroupId.initiate(brokerGroupId),
							).unwrap();
							userDocs.push(...docs);
						} catch (e) {
							console.error(
								`Error getting broker group users for broker group ${brokerGroupId}`,
								e,
							);
						}
					}
					try {
						const supportUsers = await Promise.all(
							(userDoc?.supportUsers ?? []).map(async (docRef) => {
								try {
									return await getDoc(docRef);
								} catch (e) {
									return await resolveFirebasePath(docRef);
									// const segments = docRef?.['_path']?.segments;
									// const segmentDocRef = doc(firestore, segments[0], segments[1]);
									// return await getDoc(segmentDocRef);
								}
							}),
						).then((r) => r.filter((d) => d != null));
						userDocs.push(
							...(await Promise.all(supportUsers.map(async (d) => parseUserDoc(d)))),
						);
						// userDocs.push(...supportUsers.map(parseUserDoc));
					} catch (e) {
						console.error(`Error getting support users for id ${userId}`, e);
					}
				} else {
					const docs = await dispatch(
						userApi.endpoints.getUsersByBrokerGroupId.initiate(userDoc?.brokerGroupId),
					).unwrap();
					userDocs.push(...docs);
				}
				userDocs = userDocs
					.filter(
						(obj, index, self) => index === self.findIndex((o) => o?.id === obj?.id),
					)
					.map((d) => ({
						id: d.id,
						docId: d.id,
						...d.data,
						userId: d.id,
					}));

				return { data: userDocs };
			},
		}),
		// ids are user Firestore doc ids.
		getUsersByIds: builder.query({
			providesTags: ['USERS'],
			queryFn: async (ids = []) => {
				try {
					const docIds = Array.from(new Set(ids.filter(Boolean).map(String)));

					// One read per user: the Firestore rule is checked against each user doc, so
					// users the viewer can't see are skipped instead of failing the whole list
					// (a `documentId() in` query can't be allowed by the rule).
					const snapshots = await Promise.all(
						docIds.map((id) =>
							getDoc(doc(firestore, 'user', id)).catch((e) => {
								console.warn(`Could not read user ${id}`, e);
								return null;
							}),
						),
					);

					const parsedDocs = await Promise.all(
						snapshots.filter((d) => d?.exists()).map((d) => parseUserDoc(d)),
					);
					const data = parsedDocs.map((u) => ({ id: u.id, docId: u.id, ...u.data }));
					return { data };
				} catch (e) {
					console.error(`Error getting users by ids ${ids}`, e);
					return { error: e };
				}
			},
		}),
		updateUserSignature: builder.mutation({
			invalidatesTags: ['USER', 'USER_SIGNATURE', 'BROKER_GROUP_USERS'],
			queryFn: async ({ email, html, name, currentName }, { dispatch }) => {
				try {
					const targetEmail = email ? email.toLowerCase() : null;
					const targetUser = await dispatch(
						userApi.endpoints.getUserDetails.initiate(
							{ email: targetEmail },
							{ subscribe: false },
						),
					).unwrap();

					if (!targetUser?.userId) {
						return {
							error: {
								status: 404,
								error: `No user doc found for email: ${targetEmail}`,
							},
						};
					}

					const userId = targetUser.userId;
					const emailSignatures = targetUser.emailSignatures ?? [];

					// Signatures sit under the user's primary broker group.
					const filePath = brokerSignaturePath(targetUser.brokerGroupId, userId, name);
					const storageRef = ref(storage, filePath);
					const metadata = { customMetadata: { 'User ID': userId.toString() } };
					const htmlBlob = new Blob([html], { type: 'text/html' });
					const uploadedFile = await uploadBytes(storageRef, htmlBlob, metadata);
					const downloadUrl = await getDownloadURL(uploadedFile.ref);
					const index = emailSignatures.findIndex((sig) => sig.name === currentName);
					let updatedEmailSignatures;
					if (index !== -1) {
						updatedEmailSignatures = emailSignatures.map((sig) =>
							sig.name === currentName
								? { ...sig, name, signature: downloadUrl }
								: sig,
						);
					} else {
						updatedEmailSignatures = [
							...emailSignatures,
							{ name, signature: downloadUrl },
						];
					}

					try {
						await dispatch(
							userApi.endpoints.updateUserProfile.initiate({
								email: targetEmail,
								properties: { emailSignatures: updatedEmailSignatures },
							}),
						).unwrap();
					} catch (e) {
						console.log('Error updating user document with downloadUrl', e);
						return { error: e };
					}

					try {
						await updateMetadata(uploadedFile.ref, metadata).catch((e) => {
							console.error(`Error updating metadata for ${uploadedFile?.ref}`, e);
						});
					} catch (e) {
						console.log('Error setting doc metadata', e);
						return { error: e };
					}

					return { data: { downloadUrl } };
				} catch (error) {
					console.error('❌ Error saving user signature', error);
					return { error };
				}
			},
		}),

		deleteUserSignature: builder.mutation({
			invalidatesTags: ['USER', 'USER_SIGNATURE', 'BROKER_GROUP_USERS'],
			queryFn: async ({ name, email: emailArg }, { dispatch }) => {
				try {
					console.log('Signature to delete', {
						name,
					});
					const email = emailArg ?? msalInstance.getActiveAccount()?.username;
					const targetUser = await dispatch(
						userApi.endpoints.getUserDetails.initiate({ email }, { subscribe: false }),
					).unwrap();

					if (!targetUser?.userId) {
						return {
							error: { status: 404, error: `No user doc found for email: ${email}` },
						};
					}

					const emailSignatures = targetUser.emailSignatures ?? [];
					const updatedEmailSignatures = emailSignatures.filter(
						(item) => item.name !== name,
					);
					try {
						await dispatch(
							userApi.endpoints.updateUserProfile.initiate({
								email,
								properties: { emailSignatures: updatedEmailSignatures },
							}),
						);
					} catch (e) {
						console.log('Error updating user document with downloadUrl', e);
						return { error: e };
					}
					return { data: `deleted ${name}` };
				} catch (error) {
					console.error('❌ Error deleting user signature', error);
					return { error };
				}
			},
		}),
		// Defining an endpoint to updateproperties for user by email
		updateUserProfile: builder.mutation({
			queryFn: async ({ email: emailArg, properties }, { dispatch, getState }) => {
				const email = emailArg ? emailArg.toLowerCase() : null;
				const state = getState();
				const msEmail = state?.msalAccount?.account?.username;
				const performer =
					state?.userApi?.queries[`getUserDetails({"email":"${msEmail}"})`]?.data;
				console.log('user doc: ', performer);
				try {
					const targetUser = await dispatch(
						userApi.endpoints.getUserDetails.initiate({ email }, { subscribe: false }),
					).unwrap();

					if (!targetUser?.userId) {
						return {
							error: { status: 404, error: `No user doc found for email: ${email}` },
						};
					}

					const previousValues = Object.keys(properties ?? {}).reduce((acc, key) => {
						acc[key] = targetUser?.[key] ?? null;
						return acc;
					}, {});
					const now = Timestamp.now();
					await updateDoc(doc(firestore, 'user', targetUser.userId), {
						...properties,
						lastUpdated: now,
						lastUpdatedBy: performer?.userId ?? null,
					});

					try {
						await logAuditActions({
							actionType: 'userUpdated',
							performedBy: {
								userId: performer?.userId,
								hubspotId: performer?.hubspotId,
								email: performer?.email ?? msEmail,
								name: performer?.name,
							},
							targetUser: {
								userId: targetUser.userId,
								hubspotId: targetUser?.hubspotId,
								email: targetUser?.email ?? email,
								name: targetUser?.name,
							},
							changedFields: properties,
							previousValues,
						});
					} catch (auditError) {
						console.error('Failed to log audit action', auditError);
					}

					return { data: { success: true } };
				} catch (error) {
					return {
						error: {
							status: 'CUSTOM_ERROR',
							error: error.message,
						},
					};
				}
			},
			invalidatesTags: ['USER', 'BROKER_GROUP', 'BROKER_GROUP_USERS'],
		}),
		updateBroker: builder.mutation({
			queryFn: async ({ docId, properties }, { getState, dispatch }) => {
				try {
					const user = await getActingUser(dispatch, getState);

					await updateDoc(doc(firestore, 'brokerGroup', docId), {
						...properties,
						lastUpdated: Timestamp.now(),
						lastUpdatedBy: user?.userId ?? null,
					});

					return { data: { success: true } };
				} catch (error) {
					return {
						error: {
							status: 'CUSTOM_ERROR',
							error: error.message,
						},
					};
				}
			},
			invalidatesTags: (result, error, arg) => [
				{ type: 'BROKER_GROUP', id: arg.docId },
				'BROKER_GROUP',
				'USER',
			],
		}),
		getBranchIdByBrokerGroup: builder.query({
			providesTags: (result, error, { docId }) => {
				return [{ type: 'BROKER_GROUP', id: docId }];
			},
			queryFn: async ({ docId }, { getState, dispatch }) => {
				try {
					if (docId === 'all') {
						const userData = await getActingUser(dispatch, getState);
						const email = getState()?.msalAccount?.account?.username;
						if (!email) {
							return { error: { message: 'No user email found' } };
						}
						const brokerGroupArray = getBrokerGroupArray(userData);
						if (brokerGroupArray.length === 0) {
							return { data: [] };
						}
						const brokerGroups = await dispatch(
							userApi.endpoints.getBrokerGroups.initiate({
								ids: brokerGroupArray,
								showAdmin: false,
							}),
						).unwrap();
						const branchIds = brokerGroups
							.map((group) => group?.branchId)
							.filter((id) => id !== null && id !== undefined);
						const uniqueBranchIds = [...new Set(branchIds)];
						return { data: uniqueBranchIds };
					}

					if (!docId) {
						return { data: [] };
					}
					const brokerGroup = await dispatch(
						userApi.endpoints.getBrokerGroupDetails.initiate({ docId }),
					).unwrap();

					const branchIds = brokerGroup?.branchId ? [brokerGroup.branchId] : [];

					return { data: branchIds };
				} catch (error) {
					console.error(`Error getting branch ID(s) for brokerGroupId ${docId}`, error);
					return {
						error: {
							status: 'CUSTOM_ERROR',
							error: error.message,
						},
					};
				}
			},
		}),
	}),
});

export const {
	useGetUserDetailsQuery,
	useGetAdminUsersQuery,
	useGetAdminBrokerGroupsQuery,
	useGetBrokerGroupDetailsQuery,
	useLazyGetBrokerGroupDetailsQuery,
	useGetUsersByBrokerGroupIdQuery,
	useGetUsersByIdsQuery,
	useGetBrokerGroupUsersQuery,
	useGetBrokerGroupsQuery,
	useGetUserSignatureQuery,
	useLazyGetUserSignatureQuery,
	useLazyGetUserDetailsQuery,
	useUpdateBrokerMutation,
	useUpdateUserSignatureMutation,
	useUpdateUserProfileMutation,
	useDeleteUserSignatureMutation,
	useCreateUserFirebaseTokenMutation,
	useGetBranchIdByBrokerGroupQuery,
} = userApi;
